import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Button, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT } from '@/shared/i18n'
import {
  calculateDateDifference,
  type DateDifferenceResult,
} from '@/pages/date-difference/model/dateDifference'

const UTILITY = 'date-difference-calculator'
const DEFAULT_RANGE_DAYS = 30

interface PresetRange {
  key: string
  label: string
  description: string
  startDate: string
  endDate: string
  inclusive: boolean
  businessDays: boolean
}

function toDate(value: string): Date | null {
  if (!value) return null
  const d = new Date(value + 'T00:00:00')
  return isNaN(d.getTime()) ? null : d
}

function isWeekend(date: Date): boolean {
  const day = date.getDay()
  return day === 0 || day === 6
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function toInputDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

function formatCalendarMonth(date: Date | null, locale: string): string {
  if (!date) return '---'
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date)
}

function formatCalendarDay(date: Date | null): string {
  if (!date) return '--'
  return String(date.getDate()).padStart(2, '0')
}

export function DateDifferencePage() {
  const t = useT()
  const { locale } = useLocale()
  const localeName = localeCode(locale)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [inclusive, setInclusive] = useState(false)
  const [showBusinessDays, setShowBusinessDays] = useState(false)

  useEffect(() => {
    const today = new Date()
    setStartDate(toInputDate(today))
    setEndDate(toInputDate(addDays(today, DEFAULT_RANGE_DAYS)))
  }, [])

  const start = useMemo(() => toDate(startDate), [startDate])
  const end = useMemo(() => toDate(endDate), [endDate])

  const presets = useMemo<PresetRange[]>(() => {
    const today = new Date()

    return [
      {
        key: 'next-week',
        label: t.dateDifference.presetNextWeek,
        description: t.dateDifference.presetNextWeekHint,
        startDate: toInputDate(today),
        endDate: toInputDate(addDays(today, 7)),
        inclusive: false,
        businessDays: false,
      },
      {
        key: 'work-sprint',
        label: t.dateDifference.presetWorkSprint,
        description: t.dateDifference.presetWorkSprintHint,
        startDate: toInputDate(today),
        endDate: toInputDate(addDays(today, 30)),
        inclusive: false,
        businessDays: true,
      },
      {
        key: 'past-week',
        label: t.dateDifference.presetPastWeek,
        description: t.dateDifference.presetPastWeekHint,
        startDate: toInputDate(addDays(today, -7)),
        endDate: toInputDate(today),
        inclusive: true,
        businessDays: false,
      },
    ]
  }, [t])

  const result = useMemo<DateDifferenceResult | null>(() => {
    if (!start || !end) return null

    return calculateDateDifference(start, end, {
      inclusive,
      includeBusinessDays: showBusinessDays,
    })
  }, [start, end, inclusive, showBusinessDays])

  const handleSwap = useCallback(() => {
    setStartDate(endDate)
    setEndDate(startDate)
    toolInvoke(UTILITY, { action: 'swap' })
  }, [startDate, endDate])

  const handleClear = useCallback(() => {
    setStartDate('')
    setEndDate('')
    setInclusive(false)
    setShowBusinessDays(false)
    toolInvoke(UTILITY, { action: 'reset' })
  }, [])

  const handlePreset = useCallback((preset: PresetRange) => {
    setStartDate(preset.startDate)
    setEndDate(preset.endDate)
    setInclusive(preset.inclusive)
    setShowBusinessDays(preset.businessDays)
    toolInvoke(UTILITY, { action: 'preset', preset: preset.key })
  }, [])

  return (
    <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.dateDifference.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.dateDifference.description}
        </p>
      </section>

      <section className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-amber-300"
        />
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)] lg:grid-rows-[auto_1fr]">
          <div className="p-4 sm:p-6 lg:col-start-1 lg:row-start-1 lg:border-r lg:pb-0">
            <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-end lg:grid-cols-1 lg:items-stretch">
              <DateField
                id="start-date"
                label={t.dateDifference.startDate}
                value={startDate}
                date={start}
                locale={localeName}
                onChange={setStartDate}
              />
              <button
                type="button"
                onClick={handleSwap}
                disabled={!startDate || !endDate}
                className="hidden h-10 w-10 items-center justify-center rounded-md border bg-background text-lg text-muted-foreground shadow-sm transition-colors hover:border-primary/40 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 md:flex lg:hidden"
                aria-label={t.dateDifference.swap}
              >
                &#8644;
              </button>
              <DateField
                id="end-date"
                label={t.dateDifference.endDate}
                value={endDate}
                date={end}
                locale={localeName}
                onChange={setEndDate}
              />
            </div>
          </div>

          <ResultPanel result={result} locale={localeName} />

          <div className="space-y-5 border-t p-4 sm:p-6 lg:col-start-1 lg:row-start-2 lg:border-r lg:border-t-0">
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold tracking-normal">
                  {t.dateDifference.quickRanges}
                </h2>
                <button
                  type="button"
                  onClick={handleSwap}
                  disabled={!startDate || !endDate}
                  className="text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-50 md:hidden"
                >
                  {t.dateDifference.swap}
                </button>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => handlePreset(preset)}
                    className="rounded-md border bg-background/70 px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="block text-sm font-medium">{preset.label}</span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      {preset.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h2 className="mb-2 text-sm font-semibold tracking-normal">
                {t.dateDifference.settings}
              </h2>
              <div className="grid gap-2 sm:grid-cols-2">
                <ToggleOption
                  label={t.dateDifference.inclusive}
                  checked={inclusive}
                  onChange={setInclusive}
                />
                <ToggleOption
                  label={t.dateDifference.businessDaysOnly}
                  checked={showBusinessDays}
                  onChange={setShowBusinessDays}
                />
              </div>
              <p className="mt-2 text-xs leading-5 text-muted-foreground">
                {inclusive
                  ? t.dateDifference.boundaryRuleInclusive
                  : t.dateDifference.boundaryRuleExclusive}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button variant="outline" onClick={handleSwap} disabled={!startDate || !endDate}>
                {t.dateDifference.swap}
              </Button>
              <Button variant="ghost" onClick={handleClear}>
                {t.common.clear}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">{t.dateDifference.howToTitle}</h2>
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-6 text-muted-foreground">
          {t.dateDifference.howTo.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">{t.dateDifference.faqTitle}</h2>
        <dl className="mt-4 space-y-4">
          {t.dateDifference.faq.map((item) => (
            <FaqItem key={item.question} q={item.question} a={item.answer} />
          ))}
        </dl>
      </section>
    </div>
  )
}

function DateField({
  id,
  label,
  value,
  date,
  locale,
  onChange,
}: {
  id: string
  label: string
  value: string
  date: Date | null
  locale: string
  onChange: (value: string) => void
}) {
  return (
    <label
      htmlFor={id}
      className="block transition-colors focus-within:border-primary/50 sm:rounded-lg sm:border sm:bg-background/70 sm:p-3 sm:shadow-inner"
    >
      <span className="mb-1 block text-sm font-medium sm:mb-3">{label}</span>
      <div className="grid gap-3 sm:grid-cols-[80px_minmax(0,1fr)] sm:items-center">
        <CalendarTile date={date} locale={locale} />
        <input
          id={id}
          type="date"
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          className="h-11 w-full min-w-0 rounded-md border border-input bg-card px-3 pr-10 text-base shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring sm:h-10 sm:text-sm"
        />
      </div>
    </label>
  )
}

function CalendarTile({ date, locale }: { date: Date | null; locale: string }) {
  return (
    <span className="hidden overflow-hidden rounded-md border bg-card text-center shadow-sm sm:block">
      <span className="block bg-primary px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-normal text-primary-foreground">
        {formatCalendarMonth(date, locale)}
      </span>
      <span className="block px-3 py-2 text-2xl font-bold leading-none tabular-nums">
        {formatCalendarDay(date)}
      </span>
    </span>
  )
}

function ToggleOption({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors',
        checked ? 'border-primary/50 bg-primary/10' : 'bg-background/70 hover:border-primary/40',
      )}
    >
      <span className="font-medium">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
      />
    </label>
  )
}

function ResultPanel({
  result,
  locale,
}: {
  result: DateDifferenceResult | null
  locale: string
}) {
  const t = useT()

  if (!result) {
    return (
      <div className="flex flex-col justify-between gap-6 bg-secondary/30 p-4 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[360px]">
        <div>
          <p className="text-sm font-semibold text-primary">{t.dateDifference.resultReady}</p>
          <h2 className="mt-3 text-2xl font-bold tracking-normal">
            {t.dateDifference.emptyResultTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.dateDifference.emptyResultDescription}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 bg-secondary/30 p-4 sm:space-y-5 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1">
      <div>
        <p className="text-sm font-semibold text-primary">{t.dateDifference.resultReady}</p>
        <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-2">
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold leading-none tabular-nums tracking-normal sm:text-5xl">
              {result.totalDays}
            </span>
            <span className="pb-0.5 text-sm font-medium text-muted-foreground">
              {t.dateDifference.calendarDays}
            </span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold leading-none tabular-nums tracking-normal sm:text-5xl">
              {formatDecimal(result.totalWeeks, locale)}
            </span>
            <span className="pb-0.5 text-sm font-medium text-muted-foreground">
              {t.dateDifference.totalWeeks}
            </span>
          </div>
        </div>
        {result.swapped && (
          <p className="mt-3 rounded-md border border-amber-300/70 bg-amber-100/70 px-3 py-2 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
            {t.dateDifference.swapped}
          </p>
        )}
      </div>

      <div className="hidden rounded-lg border bg-card/80 p-4 shadow-sm sm:block">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold tracking-normal">{t.dateDifference.timeline}</h2>
          <span className="text-xs text-muted-foreground">
            {formatDate(result.rangeStart, locale)} - {formatDate(result.rangeEnd, locale)}
          </span>
        </div>
        <DateRangeRail result={result} />
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <Metric
          label={t.dateDifference.fullWeeksAndDays}
          value={`${result.fullWeeks} ${t.dateDifference.weekShort} ${result.remainingDays} ${t.dateDifference.dayShort}`}
        />
        <Metric label={t.dateDifference.monthsApprox} value={result.months.toString()} />
        <Metric label={t.dateDifference.yearsApprox} value={result.years.toString()} />
        {result.businessDays !== null ? (
          <Metric label={t.dateDifference.businessDays} value={result.businessDays} accent />
        ) : null}
      </div>

      <p className="text-xs leading-5 text-muted-foreground">
        {t.dateDifference.approximationHint}
      </p>
    </div>
  )
}

function formatDecimal(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
}

function DateRangeRail({ result }: { result: DateDifferenceResult }) {
  const segmentCount = Math.max(1, Math.min(28, Math.abs(result.totalDays) || 1))
  const totalDays = Math.max(1, Math.abs(result.totalDays))
  const segments = Array.from({ length: segmentCount }, (_, index) => {
    const offset = Math.min(
      totalDays,
      Math.floor((index / Math.max(1, segmentCount - 1)) * totalDays),
    )
    return { weekend: isWeekend(addDays(result.rangeStart, offset)) }
  })

  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {segments.map((segment, index) => (
        <span
          key={index}
          className={cn(
            'h-3 flex-1 rounded-full',
            segment.weekend ? 'bg-amber-300/80 dark:bg-amber-400/50' : 'bg-primary/45',
          )}
        />
      ))}
    </div>
  )
}

function Metric({
  label,
  value,
  accent = false,
}: {
  label: string
  value: string | number
  accent?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2.5',
        accent ? 'bg-accent text-accent-foreground' : 'bg-background/70',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-normal">{value}</p>
    </div>
  )
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div>
      <dt className="font-medium">{q}</dt>
      <dd className="mt-1 text-sm leading-6 text-muted-foreground">{a}</dd>
    </div>
  )
}
